/**
 * Som de notificação sintetizado via Web Audio API — sem dependência de arquivo.
 * Toca um "ding" curto (duas notas em sequência), ~250ms total.
 *
 * Por que sintetizado:
 * - Zero asset adicional no bundle
 * - Browsers só permitem áudio após interação do usuário; nesse caso, falha silenciosa
 *   (NotificationContext após primeira interação geralmente está liberado)
 */

let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (audioContext) return audioContext;
  try {
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) return null;
    audioContext = new Ctor();
    return audioContext;
  } catch {
    return null;
  }
}

function playTone(
  ctx: AudioContext,
  frequency: number,
  startAt: number,
  duration: number,
  volume = 0.15,
) {
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();

  oscillator.type = "sine";
  oscillator.frequency.value = frequency;

  // Envelope ADSR simplificado para evitar clique abrupto.
  gain.gain.setValueAtTime(0, startAt);
  gain.gain.linearRampToValueAtTime(volume, startAt + 0.02);
  gain.gain.linearRampToValueAtTime(volume * 0.7, startAt + duration * 0.7);
  gain.gain.linearRampToValueAtTime(0, startAt + duration);

  oscillator.connect(gain).connect(ctx.destination);
  oscillator.start(startAt);
  oscillator.stop(startAt + duration);
}

/**
 * Toca o som de notificação. Falha silenciosa se o navegador bloquear áudio
 * (ex.: usuário ainda não interagiu com a página) ou se Web Audio não existir.
 */
export function playNotificationSound() {
  const ctx = getAudioContext();
  if (!ctx) return;

  // Se o contexto está suspenso (autoplay policy), tenta resumir.
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {
      /* navegador bloqueou — falha silenciosa */
    });
  }

  const now = ctx.currentTime;
  // "Ding-dong" — duas notas em sequência (E5 → C5).
  playTone(ctx, 659.25, now, 0.12);
  playTone(ctx, 523.25, now + 0.13, 0.18);
}
